import { acquireBodyScrollLock, releaseBodyScrollLock } from "@/lib/dom/bodyScrollLock";

const LOCK_SOURCE = "cookiebar";
const MOBILE_MEDIA_QUERY = "(max-width: 767px)";

type ListenerCleanup = () => void;

let initialized = false;
let container: HTMLElement | null = null;
let mutationObserver: MutationObserver | null = null;
let removalObserver: MutationObserver | null = null;
let mediaQueryCleanup: ListenerCleanup | null = null;
let isLocked = false;

const hasDom = () => typeof window !== "undefined" && typeof document !== "undefined";

function setLockState(shouldLock: boolean): void {
  if (!hasDom()) return;

  if (shouldLock && !isLocked) {
    acquireBodyScrollLock(LOCK_SOURCE);
    isLocked = true;
  } else if (!shouldLock && isLocked) {
    releaseBodyScrollLock(LOCK_SOURCE);
    isLocked = false;
  }
}

function evaluateLock(): void {
  if (!hasDom() || !container) {
    setLockState(false);
    return;
  }

  const isExpanded = container.classList.contains("cky-consent-bar-expand");
  if (!isExpanded) {
    setLockState(false);
    return;
  }

  let matchesMobile: boolean;
  if (typeof window.matchMedia === "function") {
    try {
      matchesMobile = window.matchMedia(MOBILE_MEDIA_QUERY).matches;
    } catch {
      matchesMobile = window.innerWidth < 768;
    }
  } else {
    matchesMobile = window.innerWidth < 768;
  }

  setLockState(matchesMobile);
}

function cleanupObservers(): void {
  mutationObserver?.disconnect();
  mutationObserver = null;

  removalObserver?.disconnect();
  removalObserver = null;

  mediaQueryCleanup?.();
  mediaQueryCleanup = null;

  container = null;

  setLockState(false);
}

function observeContainerAttributes(target: HTMLElement): void {
  mutationObserver?.disconnect();
  mutationObserver = new MutationObserver(() => evaluateLock());
  mutationObserver.observe(target, { attributeFilter: ["class", "style"], attributes: true });
}

function observeContainerRemoval(): void {
  removalObserver?.disconnect();

  removalObserver = new MutationObserver(() => {
    if (!container) return;
    if (document.body.contains(container)) return;
    cleanupObservers();
  });

  removalObserver.observe(document.body, { childList: true, subtree: true });
}

function observeViewportChanges(): void {
  mediaQueryCleanup?.();

  if (typeof window.matchMedia === "function") {
    try {
      const mq = window.matchMedia(MOBILE_MEDIA_QUERY);
      const handler = (event: MediaQueryListEvent) => {
        const matches = typeof event.matches === "boolean" ? event.matches : mq.matches;
        if (!matches) {
          setLockState(false);
        } else {
          evaluateLock();
        }
      };

      if (typeof mq.addEventListener === "function") {
        mq.addEventListener("change", handler);
        mediaQueryCleanup = () => mq.removeEventListener("change", handler);
      } else if (typeof mq.addListener === "function") {
        mq.addListener(handler);
        mediaQueryCleanup = () => mq.removeListener(handler);
      } else {
        const resizeHandler = () => evaluateLock();
        window.addEventListener("resize", resizeHandler);
        mediaQueryCleanup = () => window.removeEventListener("resize", resizeHandler);
      }

      return;
    } catch {
      // Ignore matchMedia errors (e.g., Safari private mode)
    }
  }

  const fallbackResizeHandler = () => evaluateLock();
  window.addEventListener("resize", fallbackResizeHandler);
  mediaQueryCleanup = () => window.removeEventListener("resize", fallbackResizeHandler);
}

function attachToContainer(nextContainer: HTMLElement | null): void {
  if (!nextContainer) {
    cleanupObservers();
    return;
  }

  container = nextContainer;

  observeContainerAttributes(nextContainer);
  observeContainerRemoval();
  observeViewportChanges();
  evaluateLock();
}

function handleBannerLoad(): void {
  if (!hasDom()) return;

  queueMicrotask(() => {
    const node = document.querySelector<HTMLElement>(".cky-consent-container");
    if (node) {
      attachToContainer(node);
    }
  });
}

function handleConsentUpdate(): void {
  if (!hasDom()) return;

  if (container && document.body.contains(container)) {
    setLockState(false);
    return;
  }

  cleanupObservers();
}

export function setupCookiebarScrollLock(): void {
  if (initialized || !hasDom()) return;
  initialized = true;

  document.addEventListener("cookieyes_banner_load", handleBannerLoad);
  document.addEventListener("cookieyes_preferences_show", handleBannerLoad);
  document.addEventListener("cookieyes_consent_update", handleConsentUpdate);

  // In case CookieYes injected before listeners were attached (unlikely, but safe)
  handleBannerLoad();
}

export function __dangerousResetCookiebarScrollLockForTests(): void {
  if (!hasDom()) {
    initialized = false;
    container = null;
    mutationObserver = null;
    removalObserver = null;
    mediaQueryCleanup = null;
    isLocked = false;
    return;
  }

  document.removeEventListener("cookieyes_banner_load", handleBannerLoad);
  document.removeEventListener("cookieyes_preferences_show", handleBannerLoad);
  document.removeEventListener("cookieyes_consent_update", handleConsentUpdate);

  cleanupObservers();
  initialized = false;
}
